import { defaultTo } from 'lodash';
import { BrandModel } from './BrandModel';
import { MarketModel } from './MarketModel';
import { IModel, Model, ModelRecord } from './Model';
import { PartnerModel } from './PartnerModel';
import { PaymentTypeModel } from './PaymentTypeModel';
import { RegionModel } from './RegionModel';
import { StateModel } from './StateModel';
import { StoreTypeModel } from './StoreTypeModel';

interface IStoreModelBase extends IModel {
  rowVersion: string;
  storeId: number;
  code: string | undefined;
  name: string | undefined;
  address: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  zipCode: string | undefined;
  phone: string | undefined;
  secondaryPhone: string | undefined;
  salesTaxRate: number;
  isOutdoorClinic: boolean;
  timezoneName: string | undefined;
  latitude: number;
  longitude: number;
  bookingMessage: string | undefined;
  stateId: number;
  marketId: number;
  regionId: number;
  brandId: number;
  paymentTypeId: number;
  storeTypeId: number;
  partnerId: number;
}

class StoreModelBase extends Model implements ModelRecord<IStoreModelBase> {
  storeId: number;
  code: string | undefined;
  name: string | undefined;
  address: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  zipCode: string | undefined;
  phone: string | undefined;
  secondaryPhone: string | undefined;
  salesTaxRate: number;
  isOutdoorClinic: boolean;
  timezoneName: string | undefined;
  latitude: number;
  longitude: number;
  bookingMessage: string | undefined;
  stateId: number;
  marketId: number;
  regionId: number;
  brandId: number;
  paymentTypeId: number;
  storeTypeId: number;
  partnerId: number;

  constructor(init?: Partial<IStoreModelBase>) {
    super(init);

    this.rowVersion = defaultTo(init?.rowVersion, '');
    this.storeId = defaultTo(init?.storeId, 0);
    this.code = defaultTo(init?.code, undefined);
    this.name = defaultTo(init?.name, undefined);
    this.address = defaultTo(init?.address, undefined);
    this.address2 = defaultTo(init?.address2, undefined);
    this.city = defaultTo(init?.city, undefined);
    this.zipCode = defaultTo(init?.zipCode, undefined);
    this.phone = defaultTo(init?.phone, undefined);
    this.secondaryPhone = defaultTo(init?.secondaryPhone, undefined);
    this.salesTaxRate = defaultTo(init?.salesTaxRate, 0);
    this.isOutdoorClinic = defaultTo(init?.isOutdoorClinic, false);
    this.latitude = defaultTo(init?.latitude, 0);
    this.longitude = defaultTo(init?.longitude, 0);
    this.bookingMessage = defaultTo(init?.bookingMessage, undefined);
    this.timezoneName = defaultTo(init?.timezoneName, undefined);

    this.stateId = defaultTo(init?.stateId, 0);
    this.marketId = defaultTo(init?.marketId, 0);
    this.regionId = defaultTo(init?.regionId, 0);
    this.brandId = defaultTo(init?.brandId, 0);
    this.paymentTypeId = defaultTo(init?.paymentTypeId, 0);
    this.storeTypeId = defaultTo(init?.storeTypeId, 0);
    this.partnerId = defaultTo(init?.partnerId, 0);
  }
}

export interface IStoreModel extends IStoreModelBase {
  state: StateModel;
  market: MarketModel;
  region: RegionModel;
  brand: BrandModel;
  paymentType: PaymentTypeModel;
  storeType: StoreTypeModel;
  partner: PartnerModel;
}

export class StoreModel
  extends StoreModelBase
  implements ModelRecord<IStoreModel>
{
  state: StateModel;
  market: MarketModel;
  region: RegionModel;
  brand: BrandModel;
  paymentType: PaymentTypeModel;
  storeType: StoreTypeModel;
  partner: PartnerModel;

  constructor(init?: Partial<IStoreModel>) {
    super(init);
    this.state = new StateModel(init?.state);
    this.market = new MarketModel(init?.market);
    this.region = new RegionModel(init?.region);
    this.brand = new BrandModel(init?.brand);
    this.paymentType = new PaymentTypeModel(init?.paymentType);
    this.storeType = new StoreTypeModel(init?.storeType);
    this.partner = new PartnerModel(init?.partner);
  }
}

export interface IStoreListingModel extends IStoreModelBase {
  stateName: string;
  stateAbbreviation: string;
  marketName: string;
  regionName: string;
  brandName: string;
  paymentTypeName: string;
  storeTypeName: string;
  partnerName: string;
  partnerLabel: string;
  hasHospital: boolean;
  hospitalName: string;
}

export class StoreListingModel
  extends StoreModelBase
  implements IStoreListingModel
{
  stateName: string;
  stateAbbreviation: string;
  marketName: string;
  regionName: string;
  brandName: string;
  paymentTypeName: string;
  storeTypeName: string;
  partnerName: string;
  partnerLabel: string;
  hasHospital: boolean;
  hospitalName: string;

  constructor(params: IStoreListingModel) {
    super(params);
    this.stateName = params.stateName;
    this.stateAbbreviation = params.stateAbbreviation;
    this.marketName = params.marketName;
    this.regionName = params.regionName;
    this.brandName = params.brandName;
    this.paymentTypeName = params.paymentTypeName;
    this.storeTypeName = params.storeTypeName;
    this.partnerName = params.partnerName;
    this.partnerLabel = params.partnerLabel;
    this.hasHospital = params.hasHospital;
    this.hospitalName = params.hospitalName;
  }
}
